// Externe Bibliotheken
import { FormEvent, KeyboardEvent, useContext, useEffect, useMemo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import axios from 'axios';

// Interne Module
import { PageState } from '@/model/common/PageState';
import { PaymentType } from '@/model/common/PaymentType';
import BookingData from '@/model/common/BookingData';
import * as storage from '@/_helper/storage';
import { checkIsAllowToBookService } from '@/_helper/checkAccess';
import tracking from '@/_helper/tracking';
import { convertPrice } from '@/_helper/mapper';
import { AppContext } from '@/context/AppContext';
import { buildPostRequest } from '@/_helper/apiHelper';

// Komponenten
import Header from '@/comp/header/header';
import Footer from '@/comp/footer/Footer';
import StatusPage from '@/comp/statusPage/statusPage';
import PaymentCard from '@/comp/payment/paymentCard/PaymentCard';
import Button from '@/comp/button/button';
import BackButton from '@/comp/backButton/backButton';

// Styles
import './paymentRefund.scss';
import { PaymentData } from '@/model/payment/payment';

interface PaymentRefundProps {
    type: 'refund' | 'credit';
}

const nameMapping = {
    refund: 'Erstattung',
    credit: 'Gutschrift',
};

const PaymentRefund = ({ type }: PaymentRefundProps) => {
    const navigate = useNavigate();
    const context = useContext(AppContext);
    const bookingData: BookingData = storage.get('bookingData') ?? {};
    const openAmount = bookingData.data?.frontendParams.price.open;

    const [pageState, setPageState] = useState<PageState>(PageState.Idle);
    const [alreadyTested, setAlreadyTested] = useState<boolean>(false);
    const [agbChecked, setAgbChecked] = useState<boolean>(false);

    const [paymentData, setPaymentData] = useState<PaymentData>({});
    const formValid = useMemo(() => agbChecked && (paymentData?.paymentValid ?? false), [agbChecked, paymentData]);

    useEffect(() => {
        if (
            (context?.bookingStatus !== 'OK' && context?.bookingStatus !== 'XX') ||
            !checkIsAllowToBookService('paymentRefund') ||
            !bookingData?.data.allowedInteractions.payment.refund ||
            openAmount >= 0
        ) {
            navigate('/');
            return;
        }
        document.title = nameMapping[type] + window.$documentTitle;
        document.documentElement.classList.add('redesign');
        tracking('loaded', type);
    }, [bookingData?.data.allowedInteractions.payment.refund, context?.bookingStatus, navigate, openAmount, type]);

    const handleCheckboxEvent = (e: KeyboardEvent) => {
        if (e.code === 'Enter' || e.code === 'Space') {
            e.preventDefault();
            setAgbChecked(!agbChecked);
        }
    };

    const handlePaymentChange = (content: PaymentData) => {
        setPaymentData((prev) => ({
            ...prev,
            ...content,
        }));
    };

    const handleSubmit = (e: FormEvent) => {
        tracking('interaction', type);
        e.preventDefault();
        setAlreadyTested(true);

        if (!formValid || !paymentData.paymentValid || !paymentData?.directDebit) {
            return;
        }
        setPageState(PageState.Submitting);

        axios(
            buildPostRequest(window.$serviceUrl, '/bookingData', {
                requestType: 'bookingData',
                clientId: 'customerarea',
                requestSource: 'AurumTours-Customerarea',
                bookingId: storage.get('bookingNumber'),
                action: 'updatePayment',
                hash: storage.get('access_token'),
                paymentData: {
                    paymentDataType: 'directDebit',
                    accountOwner: paymentData.directDebit.accountOwner,
                    accountNumber: paymentData.directDebit.accountNumber,
                },
            })
        )
            .then((response) => {
                if (response?.status === 200 && response.data?.data?.data?.updated === true) {
                    setPageState(PageState.Success);
                    return;
                }
                setPageState(PageState.Error);
            })
            .catch(() => {
                setPageState(PageState.Error);
            });
    };

    return (
        <div className="page-refund">
            <Header />
            {pageState === PageState.Success ? (
                <StatusPage type="success" subPage={type} />
            ) : pageState === PageState.Error ? (
                <StatusPage type="error" subPage={type} />
            ) : (
                <div className="content-wrapper">
                    <h1 className="main-headline">Bankverbindung zur Auszahlung</h1>
                    <div className="intro-row">
                        Bitte tragen Sie Ihre Bankverbindung in das untenstehende Formular ein. Die {nameMapping[type]}{' '}
                        erfolgt dann so schnell wie möglich und das Geld sollte innerhalb der nächsten 3 bis 5 Werktage
                        auf Ihrem Konto sein.
                    </div>
                    <form
                        className={`${pageState === PageState.Submitting ? ' submitting' : ''}`}
                        autoComplete="off"
                        onSubmit={handleSubmit}
                        onChange={() => tracking('interaction', type)}>
                        <PaymentCard
                            type={PaymentType.DirectDebit}
                            showRadioButton={false}
                            active={true}
                            alreadyTested={alreadyTested}
                            paymentData={paymentData}
                            setPaymentData={handlePaymentChange}
                        />
                        <section className="accept-sec">
                            <input
                                id="checkbox-accept"
                                name="checkbox-accept"
                                type="checkbox"
                                checked={agbChecked}
                                onChange={() => setAgbChecked(!agbChecked)}
                                onKeyDown={handleCheckboxEvent}
                            />
                            <div>
                                <label htmlFor="checkbox-accept">
                                    Mit dem Vorgang "Bankdaten übermitteln" bestätige ich, dass ich alle oben stehenden
                                    Daten und Eingaben überprüft habe und die Richtigkeit meiner Angaben vorliegt. Die{' '}
                                    <a
                                        href={`${window.$website}/unternehmen/agb`}
                                        rel="noopener noreferrer"
                                        target="_blank">
                                        AGBs
                                    </a>{' '}
                                    und{' '}
                                    <NavLink to="/datenschutz" target="_blank">
                                        Datenschutzhinweise
                                    </NavLink>{' '}
                                    von AurumTours habe ich gelesen und akzeptiere diese.
                                </label>
                                {alreadyTested && !agbChecked && (
                                    <div className="text-red error">
                                        Um die Zahlung durchführen zu können, müssen Sie den Bedingungen zustimmen.
                                    </div>
                                )}
                            </div>
                        </section>
                        <section className="user-action">
                            <h3 className="price-title">Erstattungsbetrag</h3>
                            <div className="price-value">{convertPrice(openAmount < 0 ? -openAmount : openAmount)}</div>
                            <small className="price-fineprint">Erstattungsbetrag für alle Reisenden</small>
                            <div className="cta-row">
                                <BackButton target="blank" />
                                <Button
                                    type="submit"
                                    valid={formValid}
                                    validated={pageState === PageState.Submitting}
                                    icon="arrowForward"
                                    label={
                                        pageState !== PageState.Submitting
                                            ? 'Bankdaten übermitteln'
                                            : 'Bankdaten werden übermittelt'
                                    }
                                />
                            </div>
                        </section>
                    </form>
                </div>
            )}
            <Footer />
        </div>
    );
};
export default PaymentRefund;
